import Profile from '../models/Profile';
import ProfileFieldInput from '../models/ProfileFieldInput';
import ProfileFieldInputVm from '../models/ProfileFieldInputVm';
import { ProfileFieldType } from '../models/ProfileField';
import ProfileFieldValue from '../models/ProfileFieldValue';
import { isEmpty } from 'lodash';

class ProfileService {
  public convertToInputs(profiles: Profile[]): ProfileFieldInputVm[] {
    const inputsVm = [] as ProfileFieldInputVm[];

    for (let profile of profiles) {
      const inputs = profile.fields.map((field) => {
        const { profilekey, fieldposition } = field;
        return {
          profilekey,
          fieldposition,
          required: profile.required && field.required,
          value: ''
        };
      });
      inputsVm.push(...inputs);
    }

    return inputsVm;
  }

  public convertValuesToInputs(
    profile: Profile,
    values: ProfileFieldValue[]
  ): ProfileFieldInputVm[] {
    return profile.fields.map((f) => {
      const { profilekey, fieldposition, required } = f;
      const fieldValue = values?.find(
        (v) => v.profilekey === profilekey && v.fieldposition === fieldposition
      );
      return {
        profilekey,
        fieldposition,
        required: profile.required && required,
        value: fieldValue?.value
      };
    });
  }

  public allRequiredProfileFieldsEntered = (
    profileFields: Profile[],
    fieldInputs: ProfileFieldInputVm[]
  ) => {
    if (profileFields != null && isEmpty(profileFields)) {
      return true;
    }
    if (profileFields?.every((field) => field.required === false)) {
      //* There are no required fields.
      return true;
    }
    if (isEmpty(fieldInputs)) {
      return undefined;
    }
    let result = true;
    for (let profile of profileFields) {
      for (let field of profile?.fields) {
        const input = fieldInputs?.find(
          (f) =>
            f.profilekey === field.profilekey &&
            f.fieldposition === field.fieldposition
        );
        if (
          profile.required &&
          (!input || (input.required && !!!input.value?.trim()))
        ) {
          result = false;
          break;
        }
      }
    }

    return result;
  };

  public anyChanges = (
    initialFieldInputs: ProfileFieldInputVm[],
    fieldInputs: ProfileFieldInputVm[]
  ) => {
    if (isEmpty(initialFieldInputs) && isEmpty(fieldInputs)) {
      return false;
    }

    if (initialFieldInputs?.length !== fieldInputs?.length) {
      return true;
    }

    let result = false;
    for (let initialInput of initialFieldInputs) {
      const input = fieldInputs?.find(
        (f) =>
          f.profilekey === initialInput.profilekey &&
          f.fieldposition === initialInput.fieldposition
      );
      if (initialInput.value !== input.value?.trim()) {
        result = true;
        break;
      }
    }

    return result;
  };

  public getControlType(type: ProfileFieldType) {
    switch (type) {
      case ProfileFieldType.TEXT:
        return 'text';
      case ProfileFieldType.NUMBER:
        return 'number';
      case ProfileFieldType.DATE:
        return 'date';
      case ProfileFieldType.IMAGE:
        return 'image';
      case ProfileFieldType.TEXTLIST:
        return 'text-list';
      default:
        throw new Error(
          `ProfileService.getControlType(): Invalid profile field ${type} was passed.`
        );
    }
  }

  public isdropDownList(type: ProfileFieldType): boolean {
    return [ProfileFieldType.TEXT, ProfileFieldType.TEXTLIST].includes(type);
  }

  public convertToProfileFieldInputs(
    vm: ProfileFieldInputVm[]
  ): ProfileFieldInput[] {
    return vm?.map((p) => {
      const { profilekey, fieldposition, value } = p;
      return {
        profilekey,
        fieldposition,
        value: value?.trim() || ''
      };
    });
  }

  public getInitials(firstname: string, lastname: string) {
    return `${firstname?.toUpperCase()[0] || ''}${
      lastname?.toUpperCase()[0] || ''
    }`;
  }

  public parseMultiProfileValue(value: string): string[] {
    if (!value) return [];
    try {
      return JSON.parse(value);
    } catch {
      return [value];
    }
  }
}

export default new ProfileService();
